import {
  type KeyboardEvent,
  cloneElement,
  isValidElement,
  useCallback,
  useEffect,
  useRef,
  useState
} from "react";

import Box from "@mui/material/Box";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";

import type { EmailBuilderMergeTags } from "./email-builder-merge-tags";
import EmailBuilderComponent from "./email-builder.component";

export enum EmailEditorTab {
  Editor = 0,
  EmailBuilder = 1
}

export type EmailEditorTabsProps = {
  emailBuilderRef: React.MutableRefObject<UnlayerEditor | null>;
  initialMessage?: string;
  initialDesign?: UnlayerDesign | null;
  showTabs?: boolean;
  onExportFromBuilder?: (html: string) => void;
  editorContent: React.ReactNode;
  tabLabels?: {
    editor?: string;
    emailBuilder?: string;
  };
  tabsSx?: Record<string, any>;
  activeTab?: EmailEditorTab;
  onTabChange?: (tab: EmailEditorTab) => void;
  setActiveDesign: (design: UnlayerDesign | null) => void;
  editorTabIsDisabled: boolean;
  setEditorTabIsDisabled: React.Dispatch<React.SetStateAction<boolean>>;
  /**
   * Callback when design is updated in the builder.
   * Used to detect changes for enabling save buttons.
   */
  onDesignChange?: () => void;
  /**
   * Template types to filter in the templates dialog.
   * When not defined, uses EMAIL_TEMPLATE_DESIGN_ONLY_TYPES.
   */
  templateTypes?: number[];
  /**
   * Callback to handle key down events in the editor content.
   * This will be passed to the editorContent if it's a valid React element.
   */
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
  /**
   * Whether this instance is active and should respond to global events.
   * When multiple instances are mounted, only the active one responds to
   * events like opening the templates modal.
   */
  isActive?: boolean;
  mergeTags?: EmailBuilderMergeTags;
  specialLinks?: UnlayerSpecialLink[];
};

export default function EmailEditorTabs({
  emailBuilderRef,
  initialMessage,
  initialDesign,
  showTabs = true,
  onExportFromBuilder,
  editorContent,
  tabLabels = {
    editor: "Editor",
    emailBuilder: "Drag & Drop Email Builder"
  },
  tabsSx,
  activeTab: controlledTab,
  onTabChange,
  setActiveDesign,
  editorTabIsDisabled,
  setEditorTabIsDisabled,
  onDesignChange,
  templateTypes,
  onKeyDown,
  isActive = true,
  mergeTags,
  specialLinks
}: EmailEditorTabsProps) {
  const [internalTab, setInternalTab] = useState<EmailEditorTab>(
    EmailEditorTab.Editor
  );

  // Support both controlled and uncontrolled modes
  const isControlled = controlledTab !== undefined;
  const currentTab = isControlled ? controlledTab : internalTab;

  const handleTabChange = useCallback(
    (_event: React.SyntheticEvent, newValue: EmailEditorTab) => {
      // When switching FROM emailBuilder TO editor, export HTML only if changes were made
      if (
        currentTab === EmailEditorTab.EmailBuilder &&
        newValue === EmailEditorTab.Editor &&
        editorTabIsDisabled
      ) {
        emailBuilderRef.current?.exportHtml((data) => {
          const htmlWithMergeTags = data.html.replace(
            /!--SUNO:(.*?)--/g,
            "{{$1}}"
          );
          onExportFromBuilder?.(htmlWithMergeTags);
        });
      }

      if (!isControlled) {
        setInternalTab(newValue);
      }

      onTabChange?.(newValue);
    },
    [
      currentTab,
      emailBuilderRef,
      onExportFromBuilder,
      isControlled,
      onTabChange,
      editorTabIsDisabled
    ]
  );

  // Reset state when showTabs changes to false
  useEffect(() => {
    if (!showTabs && !isControlled) {
      setInternalTab(EmailEditorTab.Editor);
    }
  }, [showTabs, isControlled]);

  const isEditorVisible = currentTab === EmailEditorTab.Editor;
  const isEmailBuilderVisible = currentTab === EmailEditorTab.EmailBuilder;

  return (
    <>
      {showTabs && (
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          sx={{ mb: 2, ...tabsSx }}
        >
          <Tab
            label={tabLabels.editor}
            disabled={editorTabIsDisabled}
          />
          <Tab label={tabLabels.emailBuilder} />
        </Tabs>
      )}

      <Box sx={{ display: isEditorVisible ? "block" : "none" }}>
        {onKeyDown && isValidElement(editorContent)
          ? cloneElement(editorContent, { onKeyDown })
          : editorContent}
      </Box>

      <EmailBuilderComponent
        emailBuilderRef={emailBuilderRef}
        visible={isEmailBuilderVisible}
        initialMessage={initialMessage}
        initialDesign={initialDesign}
        setActiveDesign={setActiveDesign}
        setEditorTabIsDisabled={setEditorTabIsDisabled}
        onDesignChange={onDesignChange}
        templateTypes={templateTypes}
        isActive={isActive}
        mergeTags={mergeTags}
        specialLinks={specialLinks}
      />
    </>
  );
}

/**
 * Hook to manage email editor tabs state and export functionality
 */
export function useEmailEditorTabs() {
  const emailBuilderRef = useRef<UnlayerEditor | null>(null);

  const [activeTab, setActiveTab] = useState<EmailEditorTab>(
    EmailEditorTab.Editor
  );
  const [activeDesign, setActiveDesign] = useState<UnlayerDesign | null>(null);
  const [editorTabIsDisabled, setEditorTabIsDisabled] = useState(false);

  const isEmailBuilder = activeTab === EmailEditorTab.EmailBuilder;

  const exportHtml = useCallback((): Promise<{
    html: string;
    design: UnlayerDesign;
  }> => {
    return new Promise((resolve, reject) => {
      if (!emailBuilderRef.current) {
        reject(new Error("Email builder not initialized"));
        return;
      }

      emailBuilderRef.current.exportHtml((data: any) => {
        const htmlWithMergeTags = data.html.replace(
          /!--SUNO:(.*?)--/g,
          "<!--SUNO:$1-->"
        );
        resolve({
          html: htmlWithMergeTags,
          design: data.design
        });
      });
    });
  }, []);

  const loadDesign = useCallback((design: UnlayerDesign) => {
    setActiveTab(EmailEditorTab.EmailBuilder);
    setActiveDesign(design);
    setEditorTabIsDisabled(true);
  }, []);

  const reset = useCallback(() => {
    setActiveTab(EmailEditorTab.Editor);
  }, []);

  return {
    emailBuilderRef,
    activeTab,
    setActiveTab,
    isEmailBuilder,
    exportHtml,
    loadDesign,
    reset,
    activeDesign,
    setActiveDesign,
    editorTabIsDisabled,
    setEditorTabIsDisabled
  };
}
